/** Pure helpers for translating between flat square indices and file/rank. */
import type { BoardConfig, PlayerId, Square, Vector } from "./types";

export interface FileRank {
  readonly file: number;
  readonly rank: number;
}

export function toSquare(file: number, rank: number, width: number): Square {
  return rank * width + file;
}

export function fromSquare(sq: Square, width: number): FileRank {
  return { file: sq % width, rank: Math.floor(sq / width) };
}

export function inBounds(file: number, rank: number, cfg: BoardConfig): boolean {
  return file >= 0 && file < cfg.width && rank >= 0 && rank < cfg.height;
}

/** Apply a vector to a square, returning null if it leaves the board. */
export function shift(sq: Square, v: Vector, cfg: BoardConfig): Square | null {
  const { file, rank } = fromSquare(sq, cfg.width);
  const f = file + v[0];
  const r = rank + v[1];
  return inBounds(f, r, cfg) ? toSquare(f, r, cfg.width) : null;
}

/**
 * The forward rank direction for a player's pawns. Player 0 advances up the
 * board (+1 rank); player 1 advances down (-1 rank).
 */
export function forwardDir(owner: PlayerId): number {
  return owner === 0 ? 1 : -1;
}

/** The rank a pawn promotes on for a given owner. */
export function promotionRank(owner: PlayerId, cfg: BoardConfig): number {
  return owner === 0 ? cfg.height - 1 : 0;
}

/** True if `sq` is within the deployment zone (home ranks) of `owner`. */
export function inDeployZone(sq: Square, owner: PlayerId, cfg: BoardConfig): boolean {
  const { rank } = fromSquare(sq, cfg.width);
  if (owner === 0) return rank < cfg.deployRanks;
  return rank >= cfg.height - cfg.deployRanks;
}

/** Algebraic-ish name for logging/debugging (a1, e4, ...). */
export function squareName(sq: Square, cfg: BoardConfig): string {
  const { file, rank } = fromSquare(sq, cfg.width);
  const fileChar = String.fromCharCode(97 + file);
  return `${fileChar}${rank + 1}`;
}
